import React, { Suspense, useState } from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery, useMutation } from 'react-relay'
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { AdminRole, BrandAccessEntry } from '../roles'
import { BrandAccessSelector } from './BrandAccessSelector'
import { RoleSelect } from '../components/RoleSelect'
import { FeatureFlagSelector } from '../FeatureFlagSelector'
import { cancelButtonSx } from '../styles'
import { emailError, requiredError } from '../validation'
import { UserFormDialogAccountsQuery } from './__generated__/UserFormDialogAccountsQuery.graphql'
import { UserFormDialogBrandFlagsQuery } from './__generated__/UserFormDialogBrandFlagsQuery.graphql'
import { UserFormDialogCreateMutation } from './__generated__/UserFormDialogCreateMutation.graphql'
import { UserFormDialogUpdateMutation } from './__generated__/UserFormDialogUpdateMutation.graphql'

export interface AdminUserInput {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  superuser: boolean
  accountId: string
  accountRoles: AdminRole[]
  brandAccess: BrandAccessEntry[]
  featureFlagIds: string[]
}

interface UserFormDialogProps {
  open: boolean
  user: AdminUserInput | null
  onClose: () => void
  onSaved: () => void
}

const accountsQuery = graphql`
  query UserFormDialogAccountsQuery($input: AdminListAccountsRequest!) {
    adminListAccounts(input: $input) {
      data {
        id
        name
        featureFlagIds
      }
    }
  }
`

// Loads the account's brands' flags so the user-level flag selector can hide
// flags already covered by the account or by the brands this user can access.
const brandFlagsQuery = graphql`
  query UserFormDialogBrandFlagsQuery($input: AdminListBrandsRequest!) {
    adminListBrands(input: $input) {
      data {
        id
        featureFlagIds
      }
    }
  }
`

const createUserMutation = graphql`
  mutation UserFormDialogCreateMutation($input: AdminCreateUserRequest!) {
    createUser(input: $input) {
      id
    }
  }
`

const updateUserMutation = graphql`
  mutation UserFormDialogUpdateMutation($input: AdminUpdateUserRequest!) {
    updateUser(input: $input) {
      id
    }
  }
`

/**
 * The user-level flag selector. Loads the account's brands' flags and hides any
 * flag already covered at the account level or by the brands this user can
 * access (account > brand > user).
 */
const UserFlagsSection = ({
  accountId,
  accountFeatureFlagIds,
  brandAccess,
  value,
  onChange,
  superuser,
}: {
  accountId: string
  accountFeatureFlagIds: string[]
  brandAccess: BrandAccessEntry[]
  value: string[]
  onChange: (ids: string[]) => void
  superuser: boolean
}) => {
  const data = useLazyLoadQuery<UserFormDialogBrandFlagsQuery>(
    brandFlagsQuery,
    { input: { accountId, pageNumber: 0, pageSize: 200 } },
    { fetchPolicy: 'store-and-network' }
  )
  const brands = data.adminListBrands.data ?? []
  const accessibleBrandIds = brandAccess.map((entry) => entry.brandId)
  const brandFlagIds = brands
    .filter((brand) => accessibleBrandIds.includes(brand.id))
    .flatMap((brand) => [...brand.featureFlagIds])
  const excludeIds = Array.from(
    new Set([...accountFeatureFlagIds, ...brandFlagIds])
  )
  if (superuser) {
    return (
      <FeatureFlagSelector
        key="superuser-flags"
        value={value}
        excludeIds={[]}
        onChange={onChange}
        scope="user"
      />
    )
  }

  return (
    <FeatureFlagSelector
      key="scoped-user-flags"
      value={value}
      excludeIds={excludeIds}
      onChange={onChange}
      scope="user"
    />
  )
}

const UserFormBody = ({
  user,
  onClose,
  onSaved,
}: Omit<UserFormDialogProps, 'open'>) => {
  const isEdit = user != null

  const accountsData = useLazyLoadQuery<UserFormDialogAccountsQuery>(
    accountsQuery,
    { input: { pageNumber: 0, pageSize: 200 } },
    { fetchPolicy: 'store-and-network' }
  )
  const accounts = accountsData.adminListAccounts.data ?? []

  const [accountId, setAccountId] = useState(user?.accountId ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [superuser, setSuperuser] = useState(user?.superuser ?? false)
  const [accountRoles, setAccountRoles] = useState<AdminRole[]>(
    user?.accountRoles ?? []
  )
  const [brandAccess, setBrandAccess] = useState<BrandAccessEntry[]>(
    user?.brandAccess ?? []
  )
  const [featureFlagIds, setFeatureFlagIds] = useState<string[]>(
    user?.featureFlagIds ?? []
  )
  const [featureFlagIdsBeforeSuperuser, setFeatureFlagIdsBeforeSuperuser] =
    useState<string[] | null>(null)

  const [createUser, creating] =
    useMutation<UserFormDialogCreateMutation>(createUserMutation)
  const [updateUser, updating] =
    useMutation<UserFormDialogUpdateMutation>(updateUserMutation)

  const loading = creating || updating

  const [submitted, setSubmitted] = useState(false)
  const emailErr = emailError(email)
  const accountErr = requiredError(accountId, 'Account')

  const selectedAccountFlagIds = [
    ...(accounts.find((account) => account.id === accountId)?.featureFlagIds ??
      []),
  ]

  const handleAccountChange = (nextAccountId: string) => {
    setAccountId(nextAccountId)
    // Brands differ per account, so clear any selected brand access.
    setBrandAccess([])
  }

  const handleSuperuserChange = (checked: boolean) => {
    setSuperuser(checked)
    if (checked) {
      setFeatureFlagIdsBeforeSuperuser(featureFlagIds)
      setBrandAccess([])
    } else {
      setFeatureFlagIds(featureFlagIdsBeforeSuperuser ?? [])
      setFeatureFlagIdsBeforeSuperuser(null)
    }
  }

  const handleSubmit = () => {
    setSubmitted(true)
    if (emailErr || accountErr) return

    const onError = (error: Error) => {
      console.error(error)
      toast.error(error.message || 'Unable to save user.')
    }
    const mappedBrandAccess = superuser
      ? []
      : brandAccess.map((entry) => ({
          brandId: entry.brandId,
          roles: entry.roles,
        }))

    if (isEdit) {
      updateUser({
        variables: {
          input: {
            id: user!.id,
            email,
            firstName,
            lastName,
            superuser,
            accountId,
            accountRoles,
            brandAccess: mappedBrandAccess,
            featureFlagIds,
          },
        },
        onCompleted: () => {
          toast.success('User updated.')
          onSaved()
          onClose()
        },
        onError,
      })
    } else {
      createUser({
        variables: {
          input: {
            accountId,
            email,
            firstName,
            lastName,
            superuser,
            accountRoles,
            brandAccess: mappedBrandAccess,
            featureFlagIds,
          },
        },
        onCompleted: () => {
          toast.success('User created.')
          onSaved()
          onClose()
        },
        onError,
      })
    }
  }

  return (
    <>
      <DialogContent>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
          User Details
        </Typography>
        <div className="flex gap-2">
          <TextField
            fullWidth
            margin="normal"
            label="First Name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Last Name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>

        <TextField
          fullWidth
          margin="normal"
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          error={submitted && !!emailErr}
          helperText={submitted ? emailErr ?? undefined : undefined}
        />

        <div className="flex gap-2">
          <TextField
            select
            fullWidth
            margin="normal"
            label="Account"
            value={accountId}
            onChange={(event) => handleAccountChange(event.target.value)}
            required
            error={submitted && !!accountErr}
            helperText={submitted ? accountErr ?? undefined : undefined}
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </TextField>

          <RoleSelect
            label="Account Roles"
            fullWidth
            margin="normal"
            value={accountRoles}
            onChange={setAccountRoles}
          />
        </div>

        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={superuser}
                onChange={(event) => handleSuperuserChange(event.target.checked)}
              />
            }
            label="Super User"
          />
          {superuser && (
            <FormHelperText sx={{ mt: 0, ml: 0 }}>
              The 'Super User' role gives the user access to all brands and
              features and should be reserved for authorized users only.
            </FormHelperText>
          )}
        </div>

        {accountId !== '' && !superuser && (
          <>
            <Divider sx={{ my: 2 }} />
            <Suspense
              fallback={
                <div className="py-2">
                  <CircularProgress size={20} />
                </div>
              }
            >
              <BrandAccessSelector
                accountId={accountId}
                value={brandAccess}
                onChange={setBrandAccess}
              />
            </Suspense>
          </>
        )}

        {(isEdit || superuser) && accountId !== '' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Suspense
              fallback={
                <div className="py-2">
                  <CircularProgress size={20} />
                </div>
              }
            >
              <UserFlagsSection
                accountId={accountId}
                accountFeatureFlagIds={selectedAccountFlagIds}
                brandAccess={brandAccess}
                value={featureFlagIds}
                onChange={setFeatureFlagIds}
                superuser={superuser}
              />
            </Suspense>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button
          variant="contained"
          sx={cancelButtonSx}
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
      </DialogActions>
    </>
  )
}

export const UserFormDialog = ({
  open,
  user,
  onClose,
  onSaved,
}: UserFormDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { width: '100%', maxWidth: 600 } }}
    >
      <DialogTitle>{user ? 'Edit User' : 'Add User'}</DialogTitle>
      {open && (
        <Suspense
          fallback={
            <DialogContent>
              <CircularProgress />
            </DialogContent>
          }
        >
          <UserFormBody user={user} onClose={onClose} onSaved={onSaved} />
        </Suspense>
      )}
    </Dialog>
  )
}

export default UserFormDialog
